import _ from 'lodash';
import React, { useRef, useState } from 'react';
import JSZip from 'jszip';

import { Button, Card, Col, Row, Table } from 'react-bootstrap';
import { ItemAvailability } from '../../../../../../__gqltypes__';
import Chip from '../../../components/Chip';
import EditRewardsModal from '../../../components/EditRewardsModal';
import EditValue from '../../../components/EditValue';

/**
 * Component to display rankgroups of a tournament
 */

/**
  type RewardValue = {
    id: string;
    type: string;
    amount: number;
  };
  */

export type RankGroupValue = {
  minRank: string | number;
  maxRank: string | number;
  rewards: any[];
};

type Props = {
  disabled: boolean;
  canSave?: boolean;
  enableContentPack?: boolean;
  enableCsvImport?: boolean;
  rankgroups: RankGroupValue[];
  onChangedRankgroups: (value: RankGroupValue[]) => void;
  onUpload: (rankGroups: RankGroupValue[]) => void;
  title?: string;
};

export function RankGroup({ 
  disabled,
  canSave,
  enableCsvImport,
  rankgroups,
  onChangedRankgroups,
  onUpload,
  enableContentPack,
  title
}: Props) {
  const [showEditReward, setShowEditReward] = useState(false);
  const [editingRewardIndex, setEditingRewardIndex] = useState<number | null>(null);
  const [editingRankgroupIndex, setEditingRankgroupIndex] = useState<number | null>(null);
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const canImport = enableCsvImport && (title ?? 'Rank Groups') === 'Leaderboard';

  const handleRemoveRankgroup = () =>
    rankgroups.length === 0 ? onChangedRankgroups([]) : onChangedRankgroups(rankgroups.slice(0, -1));
  const handleAddRankgroup = () =>
    onChangedRankgroups([
      ...rankgroups,
      {
        minRank: 0,
        maxRank: 0,
        rewards: [],
      },
    ]);

  const handleUpload = () => {
    onUpload(rankgroups);
  };

  const parseCsvLine = (line: string): string[] => {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        cells.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    cells.push(current);
    return cells;
  };

  const parseDelimitedLine = (line: string): string[] => {
    if (line.includes('\t')) {
      return line.split('\t');
    }
    return parseCsvLine(line);
  };

  const normalizeHeader = (value: string) => value.toLowerCase().replace(/[\s()$]/g, '');

  const cleanCellValue = (value: string) => value.replace(/_x000d_/gi, '').trim();

  const parseAmount = (value: string) => _.toNumber(cleanCellValue(value).replace(/,/g, '.'));

  const REWARD_TYPE_MAP: Record<string, { type: string; itemId?: string | null }> = {
    SPEEDSTER: { type: 'CURRENCY_BOOST', itemId: 'SPEEDSTER' },
    BOMB: { type: 'CURRENCY_BOOST', itemId: 'BOMB' },
    ARTIST_ONLY: { type: 'CURRENCY_BOOST', itemId: 'ARTIST_ONLY' },
    REMOVE2: { type: 'CURRENCY_BOOST', itemId: 'REMOVE2' },
    TEAM_SPIRIT: { type: 'CURRENCY_BOOST', itemId: 'TEAM_SPIRIT' },
    REVIVE: { type: 'CURRENCY_BOOST', itemId: 'REVIVE' },
    HURDLE_HINT: { type: 'CURRENCY_BOOST', itemId: 'HURDLE_HINT' },
    NINJA: { type: 'CURRENCY_BOOST', itemId: 'NINJA' },
  };

  const resolveReward = (label: string, amount: string) => {
    const normalized = label.trim().toUpperCase().replace(/\s+/g, '_');
    if (!normalized) {
      return null;
    }
    const mapped = REWARD_TYPE_MAP[normalized];
    if (mapped) {
      return {
        type: mapped.type,
        amount: parseAmount(amount || '0'),
        itemId: mapped.itemId ?? null,
      };
    }
    if (normalized.startsWith('CURRENCY_') || normalized === 'PROFILE_FRAME' || normalized === 'APP_SKIN') {
      return {
        type: normalized,
        amount: parseAmount(amount || '0'),
        itemId: null,
      };
    }
    return { error: `unknow reward ${label}` };
  };

  const parseTextRows = (text: string): string[][] => {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    return lines.map((line) => parseDelimitedLine(line));
  };

  const getSheetNamespace = (doc: Document) => doc.documentElement?.namespaceURI ?? null;

  const getNodesByTagName = (node: Document | Element, namespace: string | null, tag: string) => {
    return namespace
      ? Array.from(node.getElementsByTagNameNS(namespace, tag))
      : Array.from(node.getElementsByTagName(tag));
  };

  const parseSharedStrings = (xml: string) => {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    const sheetNamespace = getSheetNamespace(doc);
    const items = getNodesByTagName(doc, sheetNamespace, 'si');
    return items.map((item) => {
      const textNodes = getNodesByTagName(item, sheetNamespace, 't');
      return textNodes.map((node) => node.textContent ?? '').join('');
    });
  };

  const colToIndex = (ref: string) => {
    const letters = ref.match(/[A-Z]+/i)?.[0] ?? '';
    let index = 0;
    for (const char of letters.toUpperCase()) {
      index = index * 26 + (char.charCodeAt(0) - 64);
    }
    return Math.max(index - 1, 0);
  };

  const parseXlsxRows = async (file: File): Promise<string[][]> => {
    const data = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(data);
    const sheetXml = await zip.file('xl/worksheets/sheet1.xml')?.async('string');
    if (!sheetXml) {
      throw new Error('Sheet1 not fund in XLSX file');
    }
    const sharedXml = await zip.file('xl/sharedStrings.xml')?.async('string');
    const sharedStrings = sharedXml ? parseSharedStrings(sharedXml) : [];

    const doc = new DOMParser().parseFromString(sheetXml, 'application/xml');
    const sheetNamespace = getSheetNamespace(doc);
    const rowNodes = getNodesByTagName(doc, sheetNamespace, 'row');
    const rowMap = new Map<number, string[]>();

    rowNodes.forEach((row) => {
      const rowIndex = _.toNumber(row.getAttribute('r')) || rowMap.size + 1;
      const cells = getNodesByTagName(row, sheetNamespace, 'c');
      const rowValues: string[] = [];

      cells.forEach((cell) => {
        const cellRef = cell.getAttribute('r') || '';
        const colIndex = colToIndex(cellRef);
        const cellType = cell.getAttribute('t');
        let value = '';
        if (cellType === 'inlineStr') {
          const textNode = getNodesByTagName(cell, sheetNamespace, 't')[0];
          value = textNode?.textContent ?? '';
        } else if (cellType === 's') {
          const vNode = getNodesByTagName(cell, sheetNamespace, 'v')[0];
          const idx = _.toNumber(vNode?.textContent ?? '');
          value = sharedStrings[idx] ?? '';
        } else {
          const vNode = getNodesByTagName(cell, sheetNamespace, 'v')[0];
          value = vNode?.textContent ?? '';
        }
        rowValues[colIndex] = value;
      });

      rowMap.set(rowIndex, rowValues.map((cell) => cell ?? ''));
    });

    return Array.from(rowMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([, row]) => row);
  };

  const buildRankGroups = (rows: string[][]) => {
    if (rows.length === 0) {
      window.alert('empty file');
      return;
    }

    const headerCells = rows[0].map((cell) => normalizeHeader(String(cell ?? '')));
    const hasHeader = headerCells.some((cell) => cell.includes('minrank') || cell.includes('maxrank'));
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const headerIndex = (keyword: string) => headerCells.findIndex((cell) => cell.includes(keyword));
    const minRankIndex = hasHeader ? headerIndex('minrank') : 0;
    const maxRankIndex = hasHeader ? headerIndex('maxrank') : 1;
    const reward1Index = hasHeader ? headerIndex('reward1') : 2;
    const reward2Index = hasHeader ? headerIndex('reward2') : 4;

    const qtyIndexes = hasHeader
      ? headerCells.reduce<number[]>((acc, cell, idx) => (cell === 'qty' ? acc.concat(idx) : acc), [])
      : [3, 5];
    const reward1QtyIndex =
      hasHeader && reward1Index >= 0 ? qtyIndexes.find((idx) => idx > reward1Index) ?? 3 : 3;
    const reward2QtyIndex =
      hasHeader && reward2Index >= 0 ? qtyIndexes.find((idx) => idx > reward2Index) ?? 5 : 5;

    const newRankgroups: RankGroupValue[] = [];

    dataRows.forEach((cells) => {
      const minRank = cleanCellValue(String(cells[minRankIndex] ?? ''));
      const maxRank = cleanCellValue(String(cells[maxRankIndex] ?? ''));
      const reward1Label = cleanCellValue(String(cells[reward1Index] ?? ''));
      const reward1Qty = cleanCellValue(String(cells[reward1QtyIndex] ?? ''));
      const reward2Label = cleanCellValue(String(cells[reward2Index] ?? ''));
      const reward2Qty = cleanCellValue(String(cells[reward2QtyIndex] ?? ''));

      const hasAnyValue = minRank || maxRank || reward1Label || reward1Qty || reward2Label || reward2Qty;
      if (!hasAnyValue) {
        return;
      }

      if (!minRank || !maxRank) {
        window.alert('Csv must have both minRank and maxRank values for all rankgroups.');
        return;
      }

      const rewards: any[] = [];
      const reward1 = resolveReward(reward1Label, reward1Qty);
      if (reward1) {
        if ('error' in reward1) {
          window.alert(reward1.error);
          return;
        }
        rewards.push(reward1);
      }
      const reward2 = resolveReward(reward2Label, reward2Qty);
      if (reward2) {
        if ('error' in reward2) {
          window.alert(reward2.error);
          return;
        }
        rewards.push(reward2);
      }

      newRankgroups.push({
        minRank,
        maxRank,
        rewards,
      });
    });

    if (newRankgroups.length === 0) {
      window.alert('CSv data not valid');
      return;
    }

    onChangedRankgroups(newRankgroups);
  };

  const handleCsvImport = async (file: File) => {
    try {
      if (file.name.toLowerCase().endsWith('.xlsx')) {
        const rows = await parseXlsxRows(file);
        buildRankGroups(rows);
        return;
      }
      const text = await file.text();
      const rows = parseTextRows(text);
      buildRankGroups(rows);
    } catch (error) {
      window.alert(`failed read: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  };

  // Helpers function to update rewards in rankgroups
  /**
   * Return an updated version of the rankgroups by only modifying one reward for one rankgroup
   */
  const updateReward = (
    rankgroupIndex: number | null,
    rewardIndex: number | null,
    updates: Record<string, any>
  ): RankGroupValue[] => {
    return rankgroups.map((_rankgroup, _rankgroupIndex) =>
      rankgroupIndex === _rankgroupIndex
        ? {
            ..._rankgroup,
            rewards: _rankgroup.rewards.map((_reward, _rewardIndex) =>
              rewardIndex === _rewardIndex ? { ..._reward, ...updates } : _reward
            ),
          }
        : _rankgroup
    );
  };
  /**
   * Return an updated version of the rankgroups by only adding one reward for the selected rankgroup
   */
  const addReward = (rankgroupIndex: number): RankGroupValue[] => {
    return rankgroups.map((_rankgroup, _rankgroupIndex) =>
      rankgroupIndex === _rankgroupIndex
        ? {
            ..._rankgroup,
            rewards: [..._rankgroup.rewards, { type: '', amount: 0 }],
          }
        : _rankgroup
    );
  };

  /**
   * Return an updated version of the rankgroups by only adding one reward for the selected rankgroup
   */
  const removeReward = (rankgroupIndex: number): RankGroupValue[] => {
    return rankgroups.map((_rankgroup, _rankgroupIndex) =>
      rankgroupIndex === _rankgroupIndex
        ? {
            ..._rankgroup,
            rewards: _rankgroup.rewards.length === 0 ? [] : _rankgroup.rewards.slice(0, -1),
          }
        : _rankgroup
    );
  };

  /**
   * On reward selected, save to state curent selected reward and allow edit reward to be displayed
   */
  const handleShowEditReward = (rankgroupIndex: number, rewardIndex: number) => {
    setEditingRankgroupIndex(rankgroupIndex);
    setEditingRewardIndex(rewardIndex);
    setShowEditReward(true);
  };

  return (
    <Card className="mt-3 mb-3">
      <Card.Body className="text-center">
        <Card.Title>{title ?? 'Rank Groups'}</Card.Title>
        <EditRewardsModal
          disableMonthlyBuff
          disableContentPackReward={!enableContentPack}
          isEdit={
            !_.isNil(editingRankgroupIndex) &&
            !_.isNil(editingRewardIndex) &&
            rankgroups[editingRankgroupIndex].rewards[editingRewardIndex].type !== ''
          }
          show={showEditReward}
          tier={null}
          level={0}
          reward={
            !_.isNil(editingRankgroupIndex) && !_.isNil(editingRewardIndex)
              ? rankgroups[editingRankgroupIndex]?.rewards[editingRewardIndex]
              : ''
          }
          handleClose={() => setShowEditReward(false)}
          handleSave={(newReward) => {
            setShowEditReward(false);
            onChangedRankgroups(updateReward(editingRankgroupIndex, editingRewardIndex, newReward));
          }}
          liveOpsAvailabilities={[ItemAvailability.EARN]}
        />
        <Table bordered responsive hover>
          <thead className="text-center">
            <tr>
              <th>Rank group</th>
              <th>min rank</th>
              <th>max rank</th>
              <th>Rewards</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {rankgroups.map((rankgroup, rankgroupIndex) => (
              <tr key={rankgroupIndex}>
                <td className="align-middle">{rankgroupIndex + 1}</td>
                <td className="align-middle">
                  <EditValue
                    name=""
                    value={rankgroup.minRank}
                    handleValueChange={(minRank) =>
                      onChangedRankgroups(
                        rankgroups.map((element, i) => (i === rankgroupIndex ? { ...element, minRank } : element))
                      )
                    }
                  />
                </td>
                <td className="align-middle">
                  <EditValue
                    name=""
                    value={rankgroup.maxRank}
                    handleValueChange={(maxRank) =>
                      onChangedRankgroups(
                        rankgroups.map((element, i) => (i === rankgroupIndex ? { ...element, maxRank } : element))
                      )
                    }
                  />
                </td>

                <td className="align-middle">
                  <Col>
                    {rankgroup.rewards.map((reward, rewardIndex) => (
                      <Row key={`rank group reward${rewardIndex}`}>
                        {reward?.type ? (
                          <Chip
                            reward={reward}
                            canDelete={false}
                            btnAction={() => {}}
                            globalAction={() => handleShowEditReward(rankgroupIndex, rewardIndex)}
                          />
                        ) : (
                          <Chip
                            isAdd
                            outlined
                            btnAction={() => handleShowEditReward(rankgroupIndex, rewardIndex)}
                            globalAction={() => handleShowEditReward(rankgroupIndex, rewardIndex)}
                          />
                        )}
                      </Row>
                    ))}
                  </Col>
                  <Row className="mb-3">
                    <Button
                      disabled={disabled}
                      variant="success"
                      className="align-self-center"
                      onClick={() => onChangedRankgroups(addReward(rankgroupIndex))}
                    >
                      +
                    </Button>
                    <Button
                      disabled={disabled}
                      variant="danger"
                      className="align-self-center"
                      onClick={() => onChangedRankgroups(removeReward(rankgroupIndex))}
                    >
                      X
                    </Button>
                  </Row>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {canImport && (
          <>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  handleCsvImport(file);
                }
                if (event.target) {
                  event.target.value = '';
                }
              }}
            />
            <Button
              disabled={disabled}
              variant="secondary"
              className="align-self-center"
              onClick={() => csvInputRef.current?.click()}
            >
              Import XLSX/CSV
            </Button>
          </>
        )}
        <Button disabled={disabled} variant="success" className="align-self-center ml-1" onClick={handleAddRankgroup}>
          Add a rankgroup
        </Button>
        <Button disabled={disabled} variant="danger" className="align-self-center ml-1" onClick={handleRemoveRankgroup}>
          Remove a rankgroup
        </Button>
        <Button disabled={disabled || canSave === false} className="align-self-center ml-1" onClick={handleUpload}>
          Save Changes
        </Button>
      </Card.Body>
    </Card>
  );
}
